import type { ReactNode } from "react";
import logoLight from "assets/img/layout/easyfollow-logo.png";
import { MdAdminPanelSettings, MdSchool } from "react-icons/md";
import { useAppLanguage } from "hooks/useAppLanguage";
import { getAuthPortalCopy, type AuthPortal } from "lib/authPortalCopy";

type Props = {
  portal: AuthPortal;
  backgroundOnly?: boolean;
};

const portalThemes: Record<
  AuthPortal,
  { gradient: string; icon: ReactNode; accent: string }
> = {
  admin: {
    gradient: "from-brand-600 via-indigo-700 to-navy-900",
    icon: <MdAdminPanelSettings className="h-7 w-7" />,
    accent: "bg-brand-400/20 text-white",
  },
  instructor: {
    gradient: "from-teal-600 via-emerald-700 to-navy-900",
    icon: <MdSchool className="h-7 w-7" />,
    accent: "bg-teal-400/20 text-white",
  },
};

export default function AuthPortalHero({ portal, backgroundOnly = false }: Props) {
  const language = useAppLanguage();
  const t = getAuthPortalCopy(portal, language);
  const theme = portalThemes[portal];

  return (
    <div className={`overflow-hidden ${backgroundOnly ? "absolute inset-0 block min-h-screen w-full" : "relative hidden min-h-screen flex-1 md:block lg:w-[48%]"}`}>
      <div aria-hidden className={`absolute inset-0 bg-gradient-to-br ${theme.gradient}`} />
      <div aria-hidden className="absolute inset-0 opacity-40 [background-image:linear-gradient(rgba(255,255,255,.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.08)_1px,transparent_1px)] [background-size:42px_42px]" />
      <div aria-hidden className="absolute -left-32 top-12 h-80 w-80 rounded-full bg-teal-300/20 blur-3xl" />
      <div aria-hidden className="absolute -bottom-32 right-0 h-96 w-96 rounded-full bg-cyan-300/20 blur-3xl" />

      {!backgroundOnly && <div className="relative flex min-h-screen items-center justify-center p-8 lg:p-14">
        <div className="w-full max-w-[560px] text-white">
          <div className="mb-12 flex items-center gap-3">
            <div className={`flex h-12 w-12 items-center justify-center rounded-2xl border border-white/20 ${theme.accent} shadow-lg backdrop-blur`}>{theme.icon}</div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-white/60">Easyfollow Platform</p>
              <p className="mt-1 text-sm font-medium text-white/90">{t.portalLabel}</p>
            </div>
          </div>

          <p className="text-sm font-semibold text-white/60">{language === "ar" ? "مساحتك التعليمية" : "Your learning workspace"}</p>
          <h2 className="mt-4 max-w-lg text-4xl font-black leading-[1.2] tracking-tight lg:text-5xl">{t.heroTitle}</h2>
          <p className="mt-6 max-w-md text-base leading-8 text-white/75">{t.heroSubtitle}</p>

          <div className="mt-10 grid gap-3 sm:grid-cols-3">
            {t.heroBullets.map((item, index) => (
              <div key={item} className="rounded-2xl border border-white/15 bg-white/[0.08] p-4 backdrop-blur-sm">
                <span className="text-2xl font-black text-white/35">0{index + 1}</span>
                <p className="mt-5 text-sm font-semibold leading-6 text-white/90">{item}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 flex items-center justify-between border-t border-white/15 pt-6 text-xs text-white/60">
            <div className="flex items-center gap-2"><img src={logoLight} alt="Easyfollow Platform" className="h-6 w-auto opacity-90" /><span>Easyfollow Platform</span></div>
            <span>{language === "ar" ? "دخول آمن" : "Secure access"}</span>
          </div>
        </div>
      </div>}
    </div>
  );
}
